import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { worksApi } from '../../services/worksApi';

/** 举报类型（与运维平台举报处理列表口径一致：violation/copyright/spam/other） */
const REPORT_REASONS: { value: string; label: string }[] = [
  { value: 'violation', label: '违规内容' },
  { value: 'copyright', label: '侵权（抄袭/盗用）' },
  { value: 'spam', label: '垃圾信息' },
  { value: 'other', label: '其他问题' },
];

/**
 * 作品举报弹层（方案 3.4 C 端举报入口）：提交落运维平台举报工单
 * （admin_work_reports），由管理端「举报处理」列表跟进处置。
 * createPortal 挂 body，避免被预览页右栏/全屏播放器等层叠上下文裁切；
 * 未登录场景由调用方先弹登录框（pendingAction 登录成功后回到本弹层）。
 */
export const ReportDialog: React.FC<{
  open: boolean;
  workId: string;
  onClose: () => void;
}> = ({ open, workId, onClose }) => {
  const [reasonType, setReasonType] = useState('violation');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 每次打开重置表单，避免上一次的半填内容残留
  useEffect(() => {
    if (open) {
      setReasonType('violation');
      setDetail('');
      setSubmitting(false);
    }
  }, [open]);

  // Esc 关闭（提交中不可关，防止误触丢失提交结果提示）
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await worksApi.reportWork(workId, reasonType, detail.trim());
      if (res.duplicate) {
        toast.info('该作品已有待处理的举报，请耐心等待');
      } else {
        toast.success('举报已提交，感谢反馈');
      }
      onClose();
    } catch (e: any) {
      toast.error(e?.message || '举报提交失败，请稍后重试');
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[350] flex items-center justify-center"
      onClick={() => {
        if (!submitting) onClose();
      }}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
    >
      <div
        className="w-[360px] rounded-2xl bg-[#1E1E1E] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[16px] font-medium leading-[16px] text-white">举报作品</p>
        <p className="mt-2 text-[12px] leading-[18px] text-[#8A8A8A]">
          请选择举报类型，管理员将尽快核实处理
        </p>

        <div className="mt-5 flex flex-col gap-2">
          {REPORT_REASONS.map((r) => {
            const selected = reasonType === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setReasonType(r.value)}
                className={`flex h-10 w-full cursor-pointer items-center gap-3 rounded-lg border px-3 text-left text-[14px] leading-[14px] transition-colors ${
                  selected
                    ? 'border-white/60 bg-[#2A2A2A] text-white'
                    : 'border-white/10 bg-[#262626] text-[#CCCCCC] hover:border-white/30'
                }`}
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                    selected ? 'border-white' : 'border-white/30'
                  }`}
                >
                  {selected && <span className="h-2 w-2 rounded-full bg-white" />}
                </span>
                {r.label}
              </button>
            );
          })}
        </div>

        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value.slice(0, 500))}
          placeholder="补充说明（选填，500 字以内）"
          rows={3}
          className="mt-4 w-full resize-none rounded-lg bg-[#262626] px-3 py-2 text-[14px] leading-[22px] text-white outline-none placeholder:text-[#666666]"
        />

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-10 cursor-pointer rounded-lg px-4 text-[14px] leading-[14px] text-[#8A8A8A] transition-colors hover:text-white disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="h-10 cursor-pointer rounded-lg bg-white px-5 text-[14px] font-medium leading-[14px] text-[#121212] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? '提交中…' : '提交举报'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
